﻿import nouislider, { API } from 'nouislider';
import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';

const DISABLE_ATTRIBUTE = 'data-tk-disable';

export default class TKFormRangeslider extends TKCustomElementFactory {
    fieldElementFrom?: HTMLInputElement;
    fieldElementTo?: HTMLInputElement;
    sliderElement?: HTMLElement;
    rangeSlider?: API;
    rangeMax: number;
    rangeMin: number;
    rangeStep: number;
    decimal: number;
    disabled: boolean;

    static observedAttributes = [DISABLE_ATTRIBUTE];

    constructor() {
        super();

        this.fieldElementFrom = this.querySelector('[data-tk-range-from]') || undefined;
        this.fieldElementTo = this.querySelector('[data-tk-range-to]') || undefined;
        this.sliderElement = this.querySelector('[data-tk-slider]') || undefined;
        this.rangeMax = Number(this.getAttribute('data-tk-range-max') || 100);
        this.rangeMin = Number(this.getAttribute('data-tk-range-min'));
        this.rangeStep = Number(this.getAttribute('data-tk-range-step') || 1);
        this.decimal = Number(this.getAttribute('datak-tk-range-dec')) || 2;
        this.disabled = this.hasAttribute(DISABLE_ATTRIBUTE) && Boolean(this.getAttribute(DISABLE_ATTRIBUTE));
    }

    connectedCallback() {
        if (!(this.fieldElementFrom && this.fieldElementTo && this.sliderElement)) return;

        const valueFrom = Number(this.fieldElementFrom.value);
        const valueTo = Number(this.fieldElementTo.value);
        const from = valueFrom >= this.rangeMin ? valueFrom : this.rangeMin;
        const to = valueTo < this.rangeMax && valueTo !== 0 ? valueTo : this.rangeMax;

        this.rangeSlider = nouislider.create(this.sliderElement, {
            cssPrefix: 'tk-rangeslider-',
            start: [from, to],
            connect: true,
            range: {
                min: this.rangeMin,
                max: this.rangeMax,
            },
            step: this.rangeStep,
            format: {
                to: (value: number) => this.formatDecimal(value),
                from: (value: string) => this.formatDecimalToNumber(value),
            },
        });

        this.rangeSlider.on('update', this.updateField.bind(this));
        this.rangeSlider.on('set', this.removeIgnoreField.bind(this));

        this.disabled && this.rangeSlider.disable();

        this.registerInputListener();
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.rangeSlider?.destroy();
    }

    registerInputListener() {
        if (!(this.fieldElementFrom && this.fieldElementTo)) return;
        const updateRangeSlider = this.updateRangeSlider.bind(this);
        this.pushListener({ event: 'change', element: this.fieldElementFrom, action: updateRangeSlider });
        this.pushListener({ event: 'change', element: this.fieldElementTo, action: updateRangeSlider });
    }

    attributeChangedCallback(name: string, _oldValue: string, newValue: string) {
        if (name === DISABLE_ATTRIBUTE) {
            if (!newValue || newValue === '') {
                this.enable();
                return;
            }
            const value = newValue.toLowerCase();
            const toggler: Record<string, () => void> = {
                true: this.disable.bind(this),
                false: this.enable.bind(this),
            };

            toggler[value]();
        }
    }

    disable() {
        this.rangeSlider?.disable();
        this.disabled = true;
        this.fieldElementFrom!.disabled = true;
        this.fieldElementTo!.disabled = true;
    }

    enable() {
        this.rangeSlider?.enable();
        this.disabled = false;
        this.fieldElementFrom!.disabled = false;
        this.fieldElementTo!.disabled = false;
    }

    updateField(values: (number | string)[]) {
        if (!(this.fieldElementFrom && this.fieldElementTo)) return;
        const [from, to] = values;

        this.fieldElementFrom.value = String(from);
        this.fieldElementTo.value = String(to);
    }

    updateRangeSlider() {
        this.rangeSlider?.set([this.fieldElementFrom?.value || 0, this.fieldElementTo?.value || 0]);
    }

    formatDecimal(value: number) {
        return value.toFixed(this.decimal);
    }

    formatDecimalToNumber(value: string) {
        return Number(Number(value).toFixed(this.decimal));
    }

    removeIgnoreField() {
        this.fieldElementFrom?.removeAttribute('data-tk-ignore-field');
        this.fieldElementTo?.removeAttribute('data-tk-ignore-field');
    }
}
